import * as ethers from 'ethers'
import type { UseFormReturn } from 'react-hook-form'

import { useCallback, useState } from 'react'
import { useWeb3 } from 'modules/blockChain/hooks/useWeb3'
import { useFormLock } from 'shared/hooks/useFormLock'
import { useTransactionSender } from 'modules/blockChain/hooks/useTransactionSender'
import { useTxAssetAllowance } from 'modules/contracts/hooks/useTxAssetAllowance'
import { useSummOrNull } from 'modules/pools/hooks/useSummOrNull'
import { useGasEstimateResets } from 'modules/pools/hooks/useGasEstimateResets'

import { ContractRociPayment } from 'modules/contracts/contracts'
import type { FormValues, SuccessData } from './types'
import { logGroup } from 'shared/utils/logGroup'
import { strToWei, weiToStr } from 'modules/blockChain/utils/parseWei'
import * as errors from 'shared/constants/errors'
import { useAssetsData } from 'modules/pools/hooks/useAssetsData'
import {
  REPAYMENT_CONFIRM_CLICK_EVENT,
  REPAYMENT_CONFIRM_FAIL_EVENT,
  REPAYMENT_CONFIRM_SUCCESS_EVENT,
  REPAYMENT_EDIT_CLICK_EVENT,
  REPAYMENT_SUBMIT_CLICK_EVENT,
} from 'modules/analytics/events'
import { useFirebase } from 'modules/analytics/firebase'
import { useAssetBalance } from 'modules/pools/hooks/useAssetBalance'
import { PoolAsset } from 'modules/pools/constants/poolAssets'

type Args = {
  loanId: string
  onSuccess: (successData: SuccessData) => void
  formMethods: UseFormReturn<FormValues>
}

export function useRepaymentSubmit({ loanId, onSuccess, formMethods }: Args) {
  const { chainId } = useWeb3()
  const [isSubmitting, setSubmitting] = useState(false)
  const rociPayment = ContractRociPayment.useContractWeb3()
  const { isLocked, lockForm, unlockForm } = useFormLock()
  const { getAssetDecimals } = useAssetsData()
  const { trackEvent } = useFirebase()
  const { data: WETHBalanceWei } = useAssetBalance(PoolAsset.WETH)

  /**
   * Gather Data
   */
  const gatherData = useCallback(() => {
    const { amount, borrowedAsset } = formMethods.getValues()
    if (!borrowedAsset) throw new Error(errors.borrowedAssetNotSelected)
    const amountWei = strToWei(amount, getAssetDecimals(borrowedAsset))
    return {
      amount,
      amountWei,
      borrowedAsset,
      allowAsset: borrowedAsset,
      allowAmountWei: amountWei,
      allowSpender: ContractRociPayment.chainAddress.get(chainId),
    }
  }, [formMethods, getAssetDecimals, chainId])

  /**
   * Allowance tx
   */
  const { makeAllowanceIfNeeded, txAllowance } = useTxAssetAllowance(gatherData)
  const {
    estimateGas: txAllowanceEstimateGas,
    estimatedGas: txAllowanceEstimatedGas,
    resetGas: txAllowanceResetGas,
  } = txAllowance

  /**
   * Payment tx
   */
  const populatePayment = useCallback(async () => {
    const { amountWei } = gatherData()

    logGroup('Submitting repayment', {
      'Loan id': loanId,
      Amount: ethers.utils.formatEther(amountWei),
      'Amount in wei': String(amountWei),
      'RociPayment address': rociPayment.address,
    })

    const populated = await rociPayment.populateTransaction.payment(
      loanId,
      amountWei,
      ContractRociPayment.version!,
      {
        gasLimit: 500000,
      },
    )
    return populated
  }, [rociPayment, loanId, gatherData])

  const txPayment = useTransactionSender(populatePayment)
  const {
    send: sendPayment,
    estimateGas: txPaymentEstimateGas,
    estimatedGas: txPaymentEstimatedGas,
    resetGas: txPaymentResetGas,
  } = txPayment

  /**
   * Form submitter
   */
  const submit = useCallback(
    async (formValues: FormValues) => {
      try {
        const { amount, borrowedAsset } = formValues
        if (!isLocked) {
          lockForm()
          await txPaymentEstimateGas()
          await txAllowanceEstimateGas()
          trackEvent(REPAYMENT_SUBMIT_CLICK_EVENT, {
            asset: borrowedAsset,
            amount: amount,
          })
          return
        }
        setSubmitting(true)
        const WETHDecimals = getAssetDecimals(PoolAsset.WETH)
        trackEvent(REPAYMENT_CONFIRM_CLICK_EVENT, {
          asset: borrowedAsset,
          amount: amount,
          weth_balance: WETHBalanceWei
            ? weiToStr(WETHBalanceWei, WETHDecimals)
            : undefined,
        })
        await makeAllowanceIfNeeded()
        const txRepaymentRes = await sendPayment()
        setSubmitting(false)
        onSuccess({ tx: txRepaymentRes, formValues })
        trackEvent(REPAYMENT_CONFIRM_SUCCESS_EVENT)
      } catch (err) {
        setSubmitting(false)
        trackEvent(REPAYMENT_CONFIRM_FAIL_EVENT)
        throw err
      }
    },
    [
      isLocked,
      WETHBalanceWei,
      getAssetDecimals,
      trackEvent,
      makeAllowanceIfNeeded,
      sendPayment,
      onSuccess,
      lockForm,
      txPaymentEstimateGas,
      txAllowanceEstimateGas,
    ],
  )

  useGasEstimateResets(isLocked, txPaymentResetGas, txAllowanceResetGas)

  const estimatedGas = useSummOrNull(
    txPaymentEstimatedGas,
    txAllowanceEstimatedGas,
  )

  const handleFormUnlock = useCallback(() => {
    unlockForm()

    const { amount, borrowedAsset } = gatherData()
    trackEvent(REPAYMENT_EDIT_CLICK_EVENT, {
      asset: borrowedAsset,
      amount,
    })
  }, [gatherData, trackEvent, unlockForm])

  return {
    submit,
    txAllowance,
    txPayment,
    isSubmitting,
    isLocked,
    estimatedGas,
    lockForm,
    unlockForm: handleFormUnlock,
  }
}
